// Web push: subscription lifecycle and capability renewal.
//
// The subscription is one per installed app; the capabilities minted from it are
// one per distributor relation, each carrying a different contact_id, a
// different notification key and the distributor's own key fingerprint — so no
// two distributors can compare what they were handed and recognise the same
// buyer. Renewal happens on every app open (and immediately after a mute
// changes): when a subscription dies, only a running app can hand out a new
// one, and renewing on every launch closes that window as cheaply as it can be
// closed.

import { b64uDecode, b64uEncode } from '$lib/crypto/base64url';
import { fingerprintOfKey } from '$lib/crypto/hash';
import { allContacts, contactById, mutedCategories } from '$lib/db/contacts';
import { db, type ContactRow } from '$lib/db/db';
import { getSetting, setSetting } from '$lib/db/settings';
import { loadManifest, refreshDirectory } from '$lib/directory/directory-repo';
import { putCapability } from '$lib/protocol/distributor-api';
import { sessionToken } from '$lib/protocol/session';
import type { GatewayEntry } from '$lib/protocol/types';

import { createCapability } from './capability';

/// Long enough that a buyer ordering once a month never falls out of the window
/// — and any received push renews it anyway (see [renewAfterPush]), so in a
/// relation with any traffic at all the window rolls forever. The 30 days only
/// ever run out on a relation that was completely dead the whole time.
const CAPABILITY_DAYS = 30;

/// The user's standing choice, surviving a lost subscription: with it we can
/// tell "push was never enabled" from "the subscription went missing and wants
/// repairing" (see [renewCapabilities]).
const SETTING_PUSH_ENABLED = 'push.enabled';
/// When capabilities were last renewed — the once-a-day brake on push-triggered
/// renewal. Every renewal path stamps it.
const SETTING_LAST_RENEWAL = 'push.lastRenewalMs';
/// When the subscription was last force-replaced after a ghost push — a short
/// brake so a queued burst of ghosts does not churn subscriptions.
const SETTING_LAST_REPLACE = 'push.lastReplaceMs';

export function pushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

async function swRegistration(): Promise<ServiceWorkerRegistration | undefined> {
  if (!('serviceWorker' in navigator)) {
    return undefined;
  }
  try {
    return (await navigator.serviceWorker.getRegistration()) ?? undefined;
  } catch {
    return undefined;
  }
}

export async function currentSubscription(): Promise<PushSubscription | null> {
  const registration = await swRegistration();
  if (registration === undefined) {
    return null;
  }
  try {
    return await registration.pushManager.getSubscription();
  } catch {
    return null;
  }
}

/** The first manifest gateway that serves webpush — the signed `vapid_key` is
 *  both the discovery signal and the subscription's application server key. */
export async function webPushGateway(): Promise<GatewayEntry | undefined> {
  const manifest = await loadManifest();
  return manifest?.gateways.find((g) => g.vapidKey !== undefined && g.keys.length > 0);
}

/** Why enabling failed — each one has its own words in the UI, because a
 *  button that blinks and says nothing teaches the user only that it is broken. */
export type EnableFailure =
  | 'permission_denied'
  | 'permission_dismissed'
  | 'no_service_worker'
  | 'no_gateway_key'
  | 'subscribe_failed';

export type EnableResult =
  | { ok: true; subscription: PushSubscription }
  | { ok: false; reason: EnableFailure; detail?: string };

/** A registration whose worker is ACTIVE — what `pushManager.subscribe` needs.
 *
 *  `getRegistration()` answers the moment a registration EXISTS, which on a
 *  fresh install is before the worker finished activating — and WebKit rejects
 *  a subscribe on a not-yet-active worker. That is exactly the first-run race:
 *  install the app, open it, tap "turn on" straight away. `ready` resolves only
 *  with an active worker; the timeout keeps a broken install from hanging the
 *  button forever. */
async function readyRegistration(timeoutMs = 15_000): Promise<ServiceWorkerRegistration | undefined> {
  if (!('serviceWorker' in navigator)) {
    return undefined;
  }
  try {
    return await Promise.race([
      navigator.serviceWorker.ready,
      new Promise<undefined>((resolve) => setTimeout(() => resolve(undefined), timeoutMs)),
    ]);
  } catch {
    return undefined;
  }
}

/** Must run inside a user gesture. The permission prompt comes FIRST — before
 *  any disk read — because the gesture that authorizes it has a shelf life and
 *  iOS measures it strictly. */
export async function enablePush(): Promise<EnableResult> {
  const permission = await Notification.requestPermission();
  if (permission === 'denied') {
    return { ok: false, reason: 'permission_denied' };
  }
  if (permission !== 'granted') {
    return { ok: false, reason: 'permission_dismissed' };
  }

  const registration = await readyRegistration();
  if (registration === undefined) {
    return { ok: false, reason: 'no_service_worker' };
  }

  // The VAPID key rides in the signed manifest, and the cached manifest may
  // predate the gateway serving webpush — enabling is worth one forced fetch.
  let gateway = await webPushGateway();
  if (gateway?.vapidKey === undefined) {
    await refreshDirectory({ force: true }).catch(() => undefined);
    gateway = await webPushGateway();
  }
  if (gateway?.vapidKey === undefined) {
    return { ok: false, reason: 'no_gateway_key' };
  }

  try {
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: b64uDecode(gateway.vapidKey) as BufferSource,
    });
    await setSetting(SETTING_PUSH_ENABLED, true).catch(() => {});
    return { ok: true, subscription };
  } catch (error) {
    // The engine's own words travel with the failure: "AbortError: …" from a
    // phone tells us what actually broke, where a bare "refused" taught nothing.
    const detail = error instanceof Error ? `${error.name}: ${error.message}` : String(error);
    console.info('[push] subscribe failed —', detail);
    return { ok: false, reason: 'subscribe_failed', detail };
  }
}

/** Unsubscribes locally. The capabilities already handed out are not chased —
 *  they lapse within the validity window, which is the protocol's revocation. */
export async function disablePush(): Promise<void> {
  await setSetting(SETTING_PUSH_ENABLED, false).catch(() => {});
  const subscription = await currentSubscription();
  await subscription?.unsubscribe().catch(() => false);
}

/// Compares the key a subscription was created with against a wanted key.
/// A null on the subscription (some engines hide it) counts as aligned — there
/// is nothing to compare, and replacing on a guess would churn for no reason.
function keyMatches(subscription: PushSubscription, wantedB64: string): boolean {
  const current = subscription.options.applicationServerKey;
  if (current === null) {
    return true;
  }
  const a = new Uint8Array(current);
  const b = b64uDecode(wantedB64);
  if (a.length !== b.length) {
    return false;
  }
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a[i]! ^ b[i]!;
  return diff === 0;
}

/// Re-pins the subscription when the manifest announces a different VAPID key —
/// what makes a gateway key rotation a publish on our side instead of every
/// user re-enabling notifications. On any failure the old subscription stands:
/// it still works until the rotation actually lands, and the next renewal
/// simply tries again.
async function alignSubscription(
  subscription: PushSubscription,
  gateway: GatewayEntry,
  registration: ServiceWorkerRegistration | undefined,
): Promise<PushSubscription> {
  if (
    registration === undefined ||
    gateway.vapidKey === undefined ||
    keyMatches(subscription, gateway.vapidKey)
  ) {
    return subscription;
  }
  try {
    await subscription.unsubscribe();
    return await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: b64uDecode(gateway.vapidKey) as BufferSource,
    });
  } catch {
    return subscription;
  }
}

async function ensureNotificationKey(contact: ContactRow): Promise<string> {
  const existing = contact.notificationKeyB64;
  if (typeof existing === 'string' && existing.length > 0) {
    return existing;
  }
  const key = b64uEncode(crypto.getRandomValues(new Uint8Array(32)));
  await db.contacts.update(contact.accountId, { notificationKeyB64: key });
  return key;
}

/** Renews capabilities for the given contacts (all with a session when omitted)
 *  against the given subscription. Split from [renewCapabilities] so the service
 *  worker — where `navigator.serviceWorker` does not exist — can pass the
 *  subscription it already holds, with its own registration for re-pinning. */
export async function renewCapabilitiesWith(
  subscription: PushSubscription,
  accountIds?: string[],
  registration?: ServiceWorkerRegistration,
): Promise<void> {
  const gateway = await webPushGateway();
  if (gateway?.vapidKey === undefined) {
    return;
  }
  subscription = await alignSubscription(subscription, gateway, registration);
  const p256dh = subscription.getKey('p256dh');
  const auth = subscription.getKey('auth');
  if (p256dh === null || auth === null) {
    return;
  }
  await setSetting(SETTING_LAST_RENEWAL, Date.now()).catch(() => {});
  const webPushKeys = {
    p256dh: b64uEncode(new Uint8Array(p256dh)),
    auth: b64uEncode(new Uint8Array(auth)),
  };
  const validUntil = new Date(Date.now() + CAPABILITY_DAYS * 86_400_000)
    .toISOString()
    .replace(/\.\d{3}Z$/, 'Z');

  const contacts =
    accountIds === undefined
      ? await allContacts()
      : (await Promise.all(accountIds.map(contactById))).filter(
          (c): c is ContactRow => c !== undefined,
        );

  for (const contact of contacts) {
    if (contact.token === null) {
      continue;
    }
    try {
      const notificationKey = await ensureNotificationKey(contact);
      const capability = await createCapability(
        {
          platform: 'webpush',
          device_token: subscription.endpoint,
          web_push_keys: webPushKeys,
          valid_until: validUntil,
          contact_id: contact.accountId,
          gateway_id: gateway.id,
          distributor: await fingerprintOfKey(contact.serverKey),
        },
        gateway.keys,
      );
      const muted = await mutedCategories(contact.accountId);
      await putCapability({
        serverUrl: contact.serverUrl,
        accountId: contact.accountId,
        token: await sessionToken(contact),
        capability,
        validUntil,
        gatewayId: gateway.id,
        notificationKey,
        mutedCategories: [...muted],
      });
    } catch {
      // Best-effort per contact: a failed renewal means late notifications from
      // this one distributor until the next open, nothing more.
    }
  }
}

/// Single-flight resubscription: syncAll renews per contact concurrently, and
/// two parallel subscribe() calls racing each other help nobody.
let repairing: Promise<PushSubscription | null> | null = null;

/// The subscription, repaired when it should exist and does not — the user
/// enabled push, permission still stands, yet the browser holds nothing (an
/// interrupted ghost replacement, an engine that dropped it). Without this a
/// failed replacement would leave the device deaf for good.
async function obtainSubscription(
  registration: ServiceWorkerRegistration,
): Promise<PushSubscription | null> {
  const existing = await registration.pushManager.getSubscription().catch(() => null);
  if (existing !== null) {
    return existing;
  }
  const enabled = await getSetting<boolean>(SETTING_PUSH_ENABLED).catch(() => undefined);
  if (enabled !== true || Notification.permission !== 'granted') {
    return null;
  }
  repairing ??= (async () => {
    const gateway = await webPushGateway();
    if (gateway?.vapidKey === undefined) {
      return null;
    }
    try {
      return await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: b64uDecode(gateway.vapidKey) as BufferSource,
      });
    } catch {
      return null;
    }
  })().finally(() => {
    repairing = null;
  });
  return repairing;
}

/** Window-side entry point: no subscription and no standing enablement is a
 *  no-op; an enabled-but-missing subscription is repaired on the way. */
export async function renewCapabilities(accountIds?: string[]): Promise<void> {
  const registration = await swRegistration();
  if (registration === undefined) {
    return;
  }
  const subscription = await obtainSubscription(registration);
  if (subscription === null) {
    return;
  }
  await renewCapabilitiesWith(subscription, accountIds, registration);
}

/** Push-triggered renewal, from the service worker: any received wake rolls the
 *  validity window forward, so a buyer who never opens the app stays reachable
 *  as long as anything at all arrives. Braked to once a day — a busy morning of
 *  messages must not become a morning of PUTs. */
export async function renewAfterPush(registration: ServiceWorkerRegistration): Promise<void> {
  const last = (await getSetting<number>(SETTING_LAST_RENEWAL).catch(() => undefined)) ?? 0;
  if (Date.now() - last < 24 * 3600 * 1000) {
    return;
  }
  const subscription = await registration.pushManager.getSubscription().catch(() => null);
  if (subscription === null) {
    return;
  }
  await renewCapabilitiesWith(subscription, undefined, registration);
}

/** The ghost answer: a push arrived for a relation this device no longer has,
 *  so the address it was sent to stops existing. The old subscription is
 *  discarded, a fresh one minted, and every CURRENT contact gets capabilities
 *  for it on the spot — the rude sender's stored copy now names a dead endpoint
 *  and earns a terminal 410 at the gateway. Revocation enforced by physics,
 *  with no state at the gateway and no attention from the user. */
export async function replaceSubscription(
  registration: ServiceWorkerRegistration,
): Promise<void> {
  // A queued burst of ghosts must not churn subscriptions — one replacement
  // per a few minutes is plenty; the first one already killed the address.
  const last = (await getSetting<number>(SETTING_LAST_REPLACE).catch(() => undefined)) ?? 0;
  if (Date.now() - last < 5 * 60 * 1000) {
    return;
  }
  await setSetting(SETTING_LAST_REPLACE, Date.now()).catch(() => {});

  const gateway = await webPushGateway();
  if (gateway?.vapidKey === undefined) {
    return;
  }
  const old = await registration.pushManager.getSubscription().catch(() => null);
  await old?.unsubscribe().catch(() => false);
  try {
    const fresh = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: b64uDecode(gateway.vapidKey) as BufferSource,
    });
    await renewCapabilitiesWith(fresh, undefined, registration);
  } catch {
    // Subscribe failed mid-replacement: the next app open repairs it
    // (obtainSubscription) — the standing enablement flag survives.
  }
}
